<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */

    import { Tooltip } from '@oat-sa-private/ui-components';
    import { Button } from '@oat-sa-private/ui-elements';

    /**
     * @typedef {Object} Props
     * @property {String} label
     * @property {String?} [ariaLabel]
     * @property {String} [skin]
     * @property {String?} [shape]
     * @property {Boolean} [disabled]
     * @property {Boolean} [visuallyDisabled]
     * @property {String} [tooltipText]
     * @property {Number?} [wrapperOrder]
     */
    /** @type {Props & { [key: string]: any }} */
    let {
        label,
        ariaLabel = false,
        skin = 'primary',
        shape = false,
        disabled = false,
        visuallyDisabled = false,
        tooltipText = '',
        wrapperOrder = null,
        ...rest
    } = $props();

    let anchorElement = $state();

    function swallowClick(event) {
        event.preventDefault();
        event.stopPropagation();
    }

    function preventActivation(event) {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();
        }
    }

    let buttonSkin = $derived(visuallyDisabled ? 'visually-disabled' : skin);
    let buttonDisabled = $derived(disabled || visuallyDisabled);
    let wrapperLabel = $derived(ariaLabel || label || void 0);
</script>

<style>
    .tooltip-nav-button {
        position: relative;
        display: inline-flex;
        border-radius: var(--radius-large);
        @add-mixin outline-focus -1rem;
    }

    .tooltip-nav-button:focus-visible::after {
        border-radius: var(--radius-large);
    }

    .tooltip-nav-button.circular {
        border-radius: var(--radius-circular);
    }

    .tooltip-nav-button.circular:focus-visible::after {
        border-radius: var(--radius-circular);
    }

    .tooltip-nav-button.pill {
        border-radius: var(--radius-pill);
    }

    .tooltip-nav-button.pill:focus-visible::after {
        border-radius: var(--radius-pill);
    }

    .button-visual {
        display: inline-flex;
    }
</style>

<span
    class="tooltip-nav-button {shape}"
    bind:this={anchorElement}
    role="button"
    tabindex={disabled ? void 0 : 0}
    aria-label={wrapperLabel}
    aria-disabled={buttonDisabled ? 'true' : void 0}
    onclick={swallowClick}
    onkeydown={preventActivation}
    style:order={wrapperOrder}>
    <span class="button-visual" aria-hidden="true">
        <Button {...rest} {label} {ariaLabel} skin={buttonSkin} {shape} disabled={buttonDisabled} />
    </span>
</span>

{#if tooltipText && !disabled}
    <Tooltip text={tooltipText} {anchorElement} />
{/if}
